/**
 * Einstellungen → Firma → Vertrauenszeile im Widget (sql/761, Antoine 28.10.2026: „es muss einfach
 * Teil des Widgets sein“).
 *
 * Die Zeile steht unten in JEDEM Anfrage-Widget (klassisches Formular, Reisegruppen, Anlass) über
 * „Powered by MAESTRO“: Sterne · Note · „756 Google‑Bewertungen seit Eröffnung 2015“, darunter
 * „Unverbindlich · Antwort meist innerhalb eines Werktags“. Gemessenes gewinnt (lib/widget-vertrauen.ts
 * § 6): mit live geschaltetem Bewertungs-Widget kommen Note und Anzahl von Google, die Felder hier
 * sind dann nur der Rückfall; ebenso schlägt die gemessene Antwortzeit (ab 20 Anfragen) den Werktag.
 *
 * Liefert den Wert als JSON im versteckten Feld `vertrauenszeile` an das umgebende Formular
 * (`CompanyHeaderForm`), bei ungültiger Eingabe den Text „ungueltig“.
 */
import { useState } from "react";
import { Input, Segmente, Toggle } from "./components";

export interface Vertrauenszeile {
  aktiv: boolean;
  googleNote: number | null;
  googleAnzahl: number | null;
  eroeffnungsjahr: number | null;
  antwort: "werktag" | "keine";
}

/**
 * Nie eingestellt (Spalte NULL, Codex-Befund #1864): der Server zeigt dann gemessene Werte (Google-Note
 * des Bewertungs-Widgets, Antwortzeit ab 20 Anfragen) — die Zeile ist also NICHT aus. Der Schalter steht
 * deshalb auf „an“; erst ein bewusstes Aus (oder jede andere Änderung) wird gespeichert.
 */
export const VERTRAUENSZEILE_NIE_GESETZT: Vertrauenszeile = { aktiv: true, googleNote: null, googleAnzahl: null, eroeffnungsjahr: null, antwort: "werktag" };

/** Gespeicherten Stand (jsonb, evtl. mit `stand`) auf die fünf Felder bringen; NULL/Unbekanntes = „nie eingestellt“. */
export function vertrauenszeileAus(roh: unknown): Vertrauenszeile {
  if (typeof roh !== "object" || roh === null) return { ...VERTRAUENSZEILE_NIE_GESETZT };
  const r = roh as Record<string, unknown>;
  const zahl = (x: unknown) => (typeof x === "number" && Number.isFinite(x) ? x : null);
  return {
    aktiv: r.aktiv === true,
    googleNote: zahl(r.googleNote),
    googleAnzahl: zahl(r.googleAnzahl),
    eroeffnungsjahr: zahl(r.eroeffnungsjahr),
    antwort: r.antwort === "keine" ? "keine" : "werktag",
  };
}

/** Eingaben prüfen (dieselben Grenzen wie die API). Leeres Feld = null. */
export function pruefeVertrauenszeile(eingabe: { aktiv: boolean; note: string; anzahl: string; jahr: string; antwort: "werktag" | "keine" }): { wert: Vertrauenszeile | null; fehler: string[] } {
  const fehler: string[] = [];
  const noteRoh = eingabe.note.trim().replace(",", ".");
  let googleNote: number | null = null;
  if (noteRoh) {
    const n = Number(noteRoh);
    if (!/^\d(\.\d)?$/.test(noteRoh) || n < 1 || n > 5) fehler.push("Die Google-Note liegt zwischen 1,0 und 5,0, mit höchstens einer Nachkommastelle (z. B. 4,7).");
    else googleNote = n;
  }
  const anzahlRoh = eingabe.anzahl.trim().replace(/[.\s]/g, "");
  let googleAnzahl: number | null = null;
  if (anzahlRoh) {
    if (!/^\d{1,8}$/.test(anzahlRoh) || Number(anzahlRoh) < 1) fehler.push("Die Anzahl der Bewertungen ist eine ganze Zahl ab 1.");
    else googleAnzahl = Number(anzahlRoh);
  }
  const jahrRoh = eingabe.jahr.trim();
  let eroeffnungsjahr: number | null = null;
  if (jahrRoh) {
    const j = Number(jahrRoh);
    if (!/^\d{4}$/.test(jahrRoh) || j < 1800 || j > new Date().getFullYear() + 1) fehler.push("Bitte das Eröffnungsjahr vierstellig eintragen (z. B. 2015).");
    else eroeffnungsjahr = j;
  }
  if ((googleNote === null) !== (googleAnzahl === null) && fehler.length === 0) {
    fehler.push("Note und Anzahl gehören zusammen — bitte beide eintragen oder beide leer lassen.");
  }
  return fehler.length > 0 ? { wert: null, fehler } : { wert: { aktiv: eingabe.aktiv, googleNote, googleAnzahl, eroeffnungsjahr, antwort: eingabe.antwort }, fehler };
}

/** Gleicher Inhalt? (Vergleich für „geändert“ im Formular — `stand` zählt nicht.) */
export function gleicheVertrauenszeile(a: Vertrauenszeile, b: Vertrauenszeile): boolean {
  return a.aktiv === b.aktiv && a.googleNote === b.googleNote && a.googleAnzahl === b.googleAnzahl && a.eroeffnungsjahr === b.eroeffnungsjahr && a.antwort === b.antwort;
}

/** Vorschautexte, wortgleich zur deutschen Zeile im Widget (Server: baueVertrauen2). */
export function vorschauTexte(v: Vertrauenszeile): { note: string | null; anzahl: string | null; zeile2: string } {
  const mitSternen = v.googleNote !== null && v.googleAnzahl !== null;
  const anzahl = mitSternen
    ? `${new Intl.NumberFormat("de-DE").format(v.googleAnzahl!)} ${v.googleAnzahl === 1 ? "Google‑Bewertung" : "Google‑Bewertungen"}${v.eroeffnungsjahr ? ` seit Eröffnung ${v.eroeffnungsjahr}` : ""}`
    : null;
  return {
    note: mitSternen ? v.googleNote!.toFixed(1).replace(".", ",") : null,
    anzahl,
    zeile2: ["Unverbindlich", ...(v.antwort === "werktag" ? ["Antwort meist innerhalb eines Werktags"] : [])].join(" · "),
  };
}

const STERN = "M12 2.5l2.8 6 6.5.7-4.9 4.4 1.4 6.4L12 16.6 6.2 20l1.4-6.4L2.7 9.2l6.5-.7z";

function Sterne({ note }: { note: number }) {
  return (
    <span className="inline-flex items-center gap-[2px]" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => {
        const anteil = Math.max(0, Math.min(1, note - i));
        const pct = `${Math.round(anteil * 100)}%`;
        return (
          <svg key={i} width="15" height="15" viewBox="0 0 24 24" className="block">
            <defs>
              <linearGradient id={`vz-vorschau-${i}`}>
                <stop offset={pct} stopColor="#b07514" />
                <stop offset={pct} stopColor="#e3d6bf" />
              </linearGradient>
            </defs>
            <path d={STERN} fill={`url(#vz-vorschau-${i})`} />
          </svg>
        );
      })}
    </span>
  );
}

const KLEIN = "font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim";
const LABEL = "block font-label-md text-label-md text-on-background dark:text-inverse-on-surface";

export function VertrauenszeileEinstellung({ wert }: { wert: unknown }) {
  const start = vertrauenszeileAus(wert);
  const stand = typeof wert === "object" && wert !== null && typeof (wert as { stand?: unknown }).stand === "string" ? (wert as { stand: string }).stand : null;
  const [aktiv, setAktiv] = useState(start.aktiv);
  const [note, setNote] = useState(start.googleNote === null ? "" : start.googleNote.toFixed(1).replace(".", ","));
  const [anzahl, setAnzahl] = useState(start.googleAnzahl === null ? "" : String(start.googleAnzahl));
  const [jahr, setJahr] = useState(start.eroeffnungsjahr === null ? "" : String(start.eroeffnungsjahr));
  const [antwort, setAntwort] = useState<"werktag" | "keine">(start.antwort);
  const { wert: geprueft, fehler } = pruefeVertrauenszeile({ aktiv, note, anzahl, jahr, antwort });
  const vorschau = geprueft ? vorschauTexte(geprueft) : null;

  return (
    <div className="min-w-0 max-w-full space-y-3 rounded-xl border border-[#F0E7D6] px-4 py-4 dark:border-white/[0.06]" data-testid="vertrauenszeile-einstellung">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Vertrauenszeile im Widget</p>
          <p className={KLEIN}>
            Steht unten in jedem Anfrage-Widget, über „Powered by MAESTRO“: Sterne, Google-Note und Zahl der Bewertungen, darunter „Unverbindlich“ und
            das Antwortversprechen. Ist ein Bewertungs-Widget mit bestätigtem Google-Eintrag live, kommen Note und Anzahl automatisch von Google — die
            Felder hier sind dann nur der Rückfall.
          </p>
        </div>
        <Toggle checked={aktiv} onChange={setAktiv} label="Vertrauenszeile im Widget zeigen" />
      </div>
      {(typeof wert !== "object" || wert === null) && (
        <p className={KLEIN} data-testid="vertrauenszeile-nie-gesetzt">
          Noch nicht eingestellt: Im Widget stehen bisher nur gemessene Werte (Google-Note des Bewertungs-Widgets, Antwortzeit ab 20 Anfragen).
          Sobald du hier etwas änderst und speicherst — auch das Ausschalten —, gilt diese Einstellung.
        </p>
      )}
      <input type="hidden" name="vertrauenszeile" value={geprueft ? JSON.stringify(geprueft) : "ungueltig"} />

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="min-w-0">
          <label htmlFor="vzNote" className={LABEL}>Google-Note (optional)</label>
          <Input id="vzNote" className="w-full" value={note} onChange={(e) => setNote(e.target.value)} placeholder="4,7" inputMode="decimal" autoComplete="off" />
        </div>
        <div className="min-w-0">
          <label htmlFor="vzAnzahl" className={LABEL}>Anzahl Bewertungen (optional)</label>
          <Input id="vzAnzahl" className="w-full" value={anzahl} onChange={(e) => setAnzahl(e.target.value)} placeholder="756" inputMode="numeric" autoComplete="off" />
        </div>
        <div className="min-w-0">
          <label htmlFor="vzJahr" className={LABEL}>Eröffnungsjahr (optional)</label>
          <Input id="vzJahr" className="w-full" value={jahr} onChange={(e) => setJahr(e.target.value)} placeholder="2015" inputMode="numeric" autoComplete="off" />
        </div>
      </div>
      <div className="space-y-1">
        <p className={LABEL}>Antwortversprechen</p>
        <Segmente
          label="Antwortversprechen"
          werte={[
            { key: "werktag", label: "Antwort meist innerhalb eines Werktags" },
            { key: "keine", label: "Keine Angabe" },
          ]}
          wert={antwort}
          onWahl={setAntwort}
        />
        <p className={KLEIN}>Sobald MAESTRO deine Antwortzeit aus mindestens 20 Anfragen kennt, steht dort die gemessene Zeit.</p>
      </div>
      {fehler.map((f) => (
        <p key={f} className="font-body-sm text-body-sm font-semibold text-error" data-testid="vertrauenszeile-fehler">
          {f}
        </p>
      ))}
      {vorschau && aktiv && (
        <div className="rounded-lg bg-white px-3 py-3 text-center text-[#1f1a14]" data-testid="vertrauenszeile-vorschau" aria-label="Vorschau">
          {vorschau.note && vorschau.anzahl && (
            <div className="flex flex-col items-center justify-center gap-[6px] text-[15px] leading-5 sm:flex-row sm:gap-0">
              <span className="inline-flex items-center gap-2">
                <Sterne note={geprueft!.googleNote!} />
                <span className="font-semibold leading-none">{vorschau.note}</span>
              </span>
              <span className="hidden px-2 text-[#b07514] sm:inline">·</span>
              <span>{vorschau.anzahl}</span>
            </div>
          )}
          <div className="mt-[6px] text-[13.5px] leading-[19px] opacity-70">{vorschau.zeile2}</div>
        </div>
      )}
      {stand && <p className={KLEIN}>Stand der Zahlen: {stand.split("-").reverse().join(".")} (im Widget im Hinweis zu den Bewertungen genannt).</p>}
    </div>
  );
}
